import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

// Variabili di colore coerenti con il resto del sito
const darkPurple = '#1a0c2e';
const accentPink = '#ff40c0';
const accentRed = '#ff4b5c';
const softPinkGlow = 'rgba(255, 64, 192, 0.1)';
const cardBackground = 'rgba(27, 12, 46, 0.75)';
const cardBorder = 'rgba(255, 64, 192, 0.3)';
const textLight = '#f0f0f0';
const textMedium = 'rgba(240, 240, 240, 0.7)';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: ${darkPurple};
  background-image: radial-gradient(ellipse at top left, rgba(255,64,192,0.3) 0%, transparent 40%),
                    radial-gradient(ellipse at bottom right, rgba(120,0,255,0.3) 0%, transparent 40%);
  font-family: 'Poppins', sans-serif;
  color: ${textLight};
`;

const ContentCard = styled.div`
  background-color: ${cardBackground};
  border-radius: 20px;
  padding: 40px;
  margin: 20px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.2), 0 0 20px ${softPinkGlow};
  border: 1px solid ${cardBorder};
  text-align: center;
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: rgba(255, 75, 92, 0.1);
  margin: 0 auto 25px;
  animation: ${pulseAnimation} 2s infinite ease-in-out;
`;

const CancelIcon = styled.div`
  color: ${accentRed};
  font-size: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: 2.2rem;
  font-weight: 700;
  color: ${accentRed};
  margin-bottom: 20px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Message = styled.p`
  font-size: 1.1rem;
  color: ${textLight};
  max-width: 500px;
  margin: 0 auto 30px auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const RedirectMessage = styled.p`
  font-size: 0.85rem;
  color: ${textMedium};
  font-style: italic;
  margin-top: 30px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    width: 100%;
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const PrimaryButton = styled.button`
  background: linear-gradient(45deg, ${accentPink}, #ff65d5);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 14px 30px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 64, 192, 0.3);
  
  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 6px 20px rgba(255, 64, 192, 0.5);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  padding: 14px 30px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-3px) scale(1.03);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PaymentCancelPage = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Auto-redirect dopo 10 secondi
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [navigate]);
  
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };
  
  const handleTryAgain = () => {
    // Naviga alla dashboard con un parametro query per aprire il modale dei crediti
    navigate('/dashboard?openCreditsModal=true');
  };
  
  return (
    <PageContainer>
      <ContentCard>
        <IconContainer>
          <CancelIcon>
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </CancelIcon>
        </IconContainer>
        
        <Title>Payment Cancelled</Title>
        <Message>
          Your payment was not completed. No charges were made to your account.
          You can try again or return to the dashboard.
        </Message>
        
        <ButtonGroup>
          <PrimaryButton onClick={handleBackToDashboard}>
            Go to Dashboard
          </PrimaryButton>
          <SecondaryButton onClick={handleTryAgain}>
            Try Again
          </SecondaryButton>
        </ButtonGroup>
        
        <RedirectMessage>
          You will be automatically redirected to the dashboard in 10 seconds...
        </RedirectMessage>
      </ContentCard>
    </PageContainer>
  );
};

export default PaymentCancelPage; 